import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Aviso, Modal } from "./piezas";
import { useIdioma } from "../lib/idioma";

/**
 * Lector de QR con la camara del propio dispositivo, para identificar al socio
 * por su carnet sin teclear nada. Lee fotogramas del video y los decodifica;
 * no envía imagenes a ningún sitio.
 */
export default function EscanerQR({
  alLeer,
  alCerrar,
  titulo,
}: {
  alLeer: (texto: string) => void;
  alCerrar: () => void;
  titulo?: string;
}) {
  const { t } = useIdioma();
  const video = useRef<HTMLVideoElement>(null);
  const lienzo = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let flujo: MediaStream | null = null;
    let animacion = 0;
    let vivo = true;

    async function arrancar() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t("b.qr.sinCamara"));
        return;
      }

      try {
        // La camara trasera es la que apunta al carnet del socio
        flujo = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
      } catch (fallo) {
        const nombre = (fallo as { name?: string })?.name;
        setError(
          nombre === "NotAllowedError"
            ? t("b.qr.sinPermiso")
            : nombre === "NotFoundError"
              ? t("b.qr.sinDispositivo")
              : t("b.qr.falloCamara"),
        );
        return;
      }

      if (!vivo || !video.current) return;
      video.current.srcObject = flujo;
      await video.current.play().catch(() => undefined);
      leer();
    }

    function leer() {
      if (!vivo) return;
      const v = video.current;
      const c = lienzo.current;

      if (v && c && v.readyState === v.HAVE_ENOUGH_DATA) {
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        const contexto = c.getContext("2d", { willReadFrequently: true });
        if (contexto) {
          contexto.drawImage(v, 0, 0, c.width, c.height);
          const imagen = contexto.getImageData(0, 0, c.width, c.height);
          const codigo = jsQR(imagen.data, imagen.width, imagen.height, { inversionAttempts: "dontInvert" });
          if (codigo?.data) {
            vivo = false;
            alLeer(codigo.data.trim());
            return;
          }
        }
      }
      animacion = requestAnimationFrame(leer);
    }

    void arrancar();

    return () => {
      vivo = false;
      cancelAnimationFrame(animacion);
      flujo?.getTracks().forEach((pista) => pista.stop());
    };
  }, [alLeer, t]);

  return (
    <Modal
      titulo={titulo ?? t("b.escanear")}
      alCerrar={alCerrar}
      acciones={
        <button className="boton secundario" onClick={alCerrar}>
          {t("g.cancelar")}
        </button>
      }
    >
      <Aviso tipo="error">{error}</Aviso>

      {!error && (
        <>
          <div className="escaner">
            <video ref={video} playsInline muted />
            <div className="escaner-marco" />
          </div>
          <p className="suave pequeno centrado" style={{ marginBottom: 0 }}>
            {t("b.qr.apunta")}
          </p>
        </>
      )}

      <canvas ref={lienzo} hidden />
    </Modal>
  );
}
